import { locationContext } from '../location/context';
/**
 * The shell, and the one place a turn is orchestrated.
 *
 * Layout: one column with a segmented control below 1024px, unchanged
 * (design doc §11.3). Above it, chat starts full width; the board PANEL does
 * not exist in the markup until the board's first non-structural element
 * (`boardPanelStore.noticeContent`), then slides in from the right and chat
 * narrows to `--chat-width` — `Header`'s toggle collapses/reopens it from
 * there, and a manual close is sticky for the thread (never auto-reopened).
 * The emergency note is neither pane — it is pinned across both, above the
 * tabs, so it is visible on the chat tab too (§4.5).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Header from './Header';
import MobileTabs, { type Pane } from './MobileTabs';
import Board from '../board/Board';
import Thread from '../chat/Thread';
import Composer from '../chat/Composer';
import ErrorBoundary from './ErrorBoundary';
import NoteCard from '../catalog/components/NoteCard';
import { useChat } from '../store/chatStore';
import { useSurface } from '../a2ui/surfaceStore';
import { surfaceProcessor } from '../a2ui/surfaceStore';
import { applyFrame } from '../a2ui/MessageProcessor';
import { createSurfaceFromState } from '../a2ui/restore';
import { dispatchTripLink, takeLastTap } from '../a2ui/actions';
import { consumeTripToken, openedTripToken, RETURN_LINE, unconsumeTripToken } from '../export/returnLink';
import { readsTripLink } from '../store/graphFamily';
import { visitorContext } from './visitorContext';
import { autoLocate, wantsAutoGps } from '../location/autoLocate';
import { reverseGeocode } from '../location/reverseGeocode';
import { acceptNavigation, readLocationSelection, useNavigation, type NavigationGate } from '../location/navigationStore';
import type { NavigationOrigin } from '../location/navigation';
import { announcesBoardUpdate } from './boardToast';
import {
  currentContextModel,
  deadModelText,
  dropDeadModelPin,
  currentModel,
  currentReasoningEffort,
  currentWorkerReasoningEffort,
  currentWorkerModel,
  hasNoBoard,
  speaksA2ui,
  useSession,
} from '../store/sessionStore';
import { applyAttachments } from '../store/attachmentsAdapter';
import { runTurn, cancelTurn } from '../transport/runClient';
import { ApiError, deployModels, getThreadState, health } from '../transport/api';
import {
  exitedResume,
  readQuestionnaireValue,
  useQuestionnaire,
  type ResumeValue,
} from '../store/questionnaireStore';
import { wireSender } from '../lib/send';
import { resumeRemoval } from '../catalog/planRemoval';
import { cn } from '../lib/cn';
import { useActivity } from '../activity/activityStore';
import NavOverlay from '../nav/NavOverlay';
import OfflineGuide from '../offline/OfflineGuide';
import { restoreUnreached, retryRestore } from './restoreRetry';
import { readDraft, saveDraft } from './draftStorage';
import NavSheet from '../nav/NavSheet';
import { useNavCard } from '../nav/navMode';
import { useNav } from '../nav/navStore';
import { isNavEvent } from '../nav/events';
import { readActivityFrame } from '../activity/types';
import { readReasoningFrame } from '../activity/reasoningTypes';
import { useBoardPanel } from '../board/boardPanelStore';
import { focusBoardCard } from '../board/focus';
import { useWorkerStream } from '../transport/workerStream';
import type { NormalizedEvent } from '../transport/events';
import type { NoteCardProps } from '../catalog/generated/schemas';
import {
  cancelDeferred,
  createReportSchedulerState,
  onEndTurn as schedulerOnEndTurn,
  onWorkerDone,
  resetReportScheduler,
} from '../chat/reportScheduler';
import {
  createSendQueueState,
  decideSend,
  dequeue as dequeueSend,
  enqueue as enqueueSend,
  hasQueued,
  resetSendQueue,
} from '../chat/sendQueue';
import { setTurnKind, useTurnKind } from '../chat/turnLock';

const WIDE = '(min-width: 1024px)';

/**
 * All three modes on every graph, and the reason `values` stayed is measured
 * rather than assumed: it is what restores a thread reopened tomorrow, and on
 * the canvas graph the `board` key rides in it, so dropping it to save the
 * transcript would cost the reload path its only source. The board itself is
 * built from `custom` while the turn runs; `values` is the floor under it.
 */
function streamModeFor(_assistantId: string): string[] {
  return ['messages', 'custom', 'values'];
}

type LocationRequest = { threadId: string; turn: number; origin: NavigationOrigin | null; locationAt: number | null;
  revision: number; resetAt: number | null; adoptedSelection?: NavigationGate['adoptedSelection'] };
function applyNavigationEvent(
  kind: 'navigation' | 'location_selection', payload: unknown, request: LocationRequest,
): void {
  const current = useSession.getState();
  const point = current.agentGeoSet ? current.agentGeo : current.geo;
  const currentOrigin: NavigationOrigin | null = point
    ? {
        lat: point.lat,
        lon: point.lon,
        source: current.agentGeoSet ? current.agentGeoMeta?.source ?? 'manual' : current.agentGeoMeta?.source ?? 'gps',
      }
    : null;
  const currentLocationAt = current.agentGeoSet
    ? current.agentGeoMeta?.at ?? null
    : current.agentGeoMeta?.at ?? request.locationAt;
  const gate: NavigationGate = {
    assistantId: current.assistantId,
    threadId: current.threadId,
    requestThreadId: request.threadId,
    currentOrigin,
    requestOrigin: request.origin,
    currentLocationAt,
    requestLocationAt: request.locationAt,
    currentTurn: useChat.getState().turn,
    requestTurn: request.turn,
    currentRevision: current.agentLocationRevision,
    requestRevision: request.revision,
    currentResetAt: current.locationResetAt,
    requestResetAt: request.resetAt,
    currentSelectionId: current.agentGeoMeta?.selectionId,
    adoptedSelection: request.adoptedSelection,
  };
  if (kind === 'navigation') { acceptNavigation(payload, gate); return; }
  const selection = readLocationSelection(payload, gate);
  if (!selection) return;
  current.setChatLocation(selection.point, { at: selection.at, label: selection.label, selectionId: selection.selection_id });
  request.revision = useSession.getState().agentLocationRevision;
  request.adoptedSelection = { origin: { ...selection.point, source: 'chat' }, at: selection.at, id: selection.selection_id };
}

export default function App() {
  const [wide, setWide] = useState(() => window.matchMedia(WIDE).matches);
  const [pane, setPane] = useState<Pane>('chat');
  const [toast, setToast] = useState<string | null>(null);
  // A device that boots with no network starts behind the curtain at once (#43).
  const [offline, setOffline] = useState(() => typeof navigator !== 'undefined' && navigator.onLine === false);
  const restoredThread = useRef<string | null>(null);
  const [restoredThreadId, setRestoredThreadId] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  // The collapsed board panel's a11y state (§11.3 review fix): `inert` rather
  // than `aria-hidden`, because `aria-hidden` only removes a subtree from the
  // accessibility TREE — its buttons stay in the tab order, so a keyboard user
  // tabs silently into an invisible panel. `inert` drops both. React 18 does not
  // type `inert` as a JSX prop (that lands in React 19), so it is set on the DOM
  // node directly, in an effect keyed on everything that decides its value.
  const boardSection = useRef<HTMLElement | null>(null);
  // The replay gate lives per CONNECTION, not per turn: a reconnect replays
  // frames the surface has already applied, and `seq` is how they say so.
  const frameGate = useRef({ lastSeq: 0 });
  // `peterhof_orch`/`peterhof_tandem`'s worker runs on a SECOND connection
  // (`workerStream.ts`), numbering its own `seq` independently — a shared
  // gate would read the worker's own first frame as a replay of the main
  // stream's (pinned in `a2ui.test.ts`, "two side-by-side streams"). Reset
  // alongside `frameGate` on `newThread()`, never per turn: like the main
  // gate, nothing here promises `seq` resets between two delegations in one
  // thread.
  const workerFrameGate = useRef({ lastSeq: 0 });
  // `peterhof_live`'s report scheduler (§chat/reportScheduler.ts) — a plain
  // object, not store state, for the same reason `abort`/`controller` above
  // are refs: nothing here needs a re-render, and the three functions that
  // touch it are called from event handlers, not rendered from.
  const reportScheduler = useRef(createReportSchedulerState()).current;
  // The queue behind «send while a turn is streaming» (`chat/sendQueue.ts`) —
  // a plain object beside `reportScheduler` for the same reason: no
  // re-render needed, touched only from event handlers and from `dispatch`'s
  // own tail.
  const sendQueue = useRef(createSendQueueState()).current;
  // WHICH turn is streaming right now — `chatStore.status` alone cannot say,
  // since a visitor's own turn and a `peterhof_live` report share the same
  // field. Set (`setTurnKind`, `chat/turnLock.ts` — a store, so the composer and
  // the buttons can lock on it too) by `dispatch`/`runReportTurn` before their
  // first `await`, cleared once that turn has actually ended; `decideSend`
  // (`chat/sendQueue.ts`) reads it to choose ignore vs queue.

  /**
   * The last bubble belonging to a given turn — the activity feed's anchor
   * (`useActivity.getState().finish`). Turn-scoped rather than "the last
   * bubble in the array": an INTERRUPTED turn's own tail can run after a
   * newer turn has already pushed its own user bubble (and by then maybe
   * more), so `bubbles[bubbles.length - 1]` would anchor the interrupted
   * turn's feed under the turn that replaced it. Falls back to the last
   * bubble overall only when nothing matches (defensive; every live turn
   * stamps at least its own user bubble with its `turn` number).
   */
  function lastBubbleOfTurn(turnNumber: number): string | null {
    const { bubbles } = useChat.getState();
    for (let at = bubbles.length - 1; at >= 0; at -= 1) {
      if (bubbles[at]!.turn === turnNumber) return bubbles[at]!.id;
    }
    return bubbles.length > 0 ? bubbles[bubbles.length - 1]!.id : null;
  }

  const session = useSession();
  const chat = useChat();
  const sendDisabled = offline || restoredThreadId !== session.threadId;
  const surface = useSurface();
  // `peterhof_agent`: no board, no attachments shim, no `MobileTabs` segmented
  // control — the chat is the whole app (§sessionStore.hasNoBoard). Its A2UI is
  // the footer's suggested replies only, drawn by `Chips` in the chat (#127).
  const noBoard = hasNoBoard(session.assistantId);
  // The mode is a PROP of the map card, so it is read from the board and from
  // nowhere else — and it survives an F5 because the board does (design §2.1).
  const navCard = useNavCard();
  const navActive = navCard !== null;
  const navSheet = useNav((state) => state.sheet);

  useEffect(() => {
    const query = window.matchMedia(WIDE);
    const onChange = () => setWide(query.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);

  // ── the worker's own stream ────────────────────────────────────────────
  // What `workerStream.ts` forwards from the SECOND connection — only
  // `a2ui`/`activity` ever reach here (`forwardableWorkerEvent`). `turn` is
  // the MAIN turn the delegation belongs to, captured once by the caller
  // (below, and in the F5 reconnect effect) rather than re-read per frame:
  // a worker's steps belong to the turn that spawned it, live or after that
  // turn has already ended (`useActivity.getState().apply` upserts into that
  // turn's feed either way — never `begin()`, so a `started` frame from the
  // worker is a CONTINUATION, never a new turn).
  const applyWorkerEvent: (event: NormalizedEvent, turn: number) => void = useCallback(
    (event: NormalizedEvent, turn: number) => {
      if (event.kind === 'a2ui') {
        applyFrame(surfaceProcessor, event.frame, workerFrameGate.current);
      } else if (event.kind === 'activity') {
        const frame = readActivityFrame(event.payload);
        if (frame) useActivity.getState().apply(frame, turn);
      } else if (event.kind === 'worker_started') {
        // `peterhof_tandem`'s queue (2026-09-15): the worker that just finished
        // started the NEXT queued job and announced it on its own stream — no
        // main turn carries this frame. Chain onto it; its steps belong to the
        // chat's current turn, since the visitor has moved on meanwhile.
        const nextTurn = useChat.getState().turn;
        useWorkerStream
          .getState()
          .start(useSession.getState().threadId, { sideThreadId: event.threadId, runId: event.runId }, (next) =>
            applyWorkerEvent(next, nextTurn),
          );
      } else if (event.kind === 'progress') {
        // `peterhof_live`'s narrator line, arriving on the worker's own side
        // stream — built into a bubble exactly like a `peterhof_orch`
        // `pa_bridge_kind=progress` message is (`chatStore.ingest`'s own
        // `progress` case).
        useChat.getState().ingest(event);
      } else if (event.kind === 'worker_done') {
        // The delegation this side stream belongs to has finished — start (or
        // defer) the report run. Not gated on `turn` at all: the job may
        // finish long after the turn that started it has ended.
        scheduleReport(event.delegationId);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  /**
   * `peterhof_live`'s report run — the graph answers with an ordinary AI
   * message under a fresh id, no user bubble, no gate, no tools
   * (§plan «Архитектура», MR 1's `on_turn_start`). Structurally the same shape
   * `dispatch` below drives (`beginTurn`/`endTurn`, the same `onEvent` handlers,
   * the same board/activity wiring), deliberately NOT sharing its
   * `AbortController`: «Стоп» must never cancel a report run nobody asked to
   * stop, and this run must never be what «Стоп» stops.
   */
  const runReportTurn = useCallback(
    async (delegationId: string) => {
      const { threadId, assistantId, userId } = useSession.getState();
      const controller = new AbortController();

      setTurnKind('report');
      useChat.getState().beginTurn(assistantId);
      const turn = useChat.getState().turn;
      useActivity.getState().begin(turn);

      const outcome = await runTurn({
        threadId,
        assistantId,
        text: '', // no visitor utterance — `input.messages` goes out as `[]`
        streamMode: streamModeFor(assistantId),
        signal: controller.signal,
        context: { user_id: userId, channel: 'web', live_report: delegationId },
        handlers: {
          onEvent: (event) => {
            useChat.getState().ingest(event);
            if (event.kind === 'a2ui') {
              applyFrame(surfaceProcessor, event.frame, frameGate.current);
            } else if (event.kind === 'activity') {
              const frame = readActivityFrame(event.payload);
              if (frame) useActivity.getState().apply(frame, turn);
            } else if (event.kind === 'reasoning') {
              const frame = readReasoningFrame(event.payload);
              if (frame) useActivity.getState().applyReasoning(frame, turn);
            } else if (event.kind === 'worker_started') {
              // Defensive completeness only — a report run holds no tools and
              // starts no job of its own, so this should never actually fire.
              useWorkerStream
                .getState()
                .start(threadId, { sideThreadId: event.threadId, runId: event.runId }, (workerEvent) =>
                  applyWorkerEvent(workerEvent, turn),
                );
            }
          },
          onReconnect: () => useChat.getState().setStatus('reconnecting'),
          onRecovered: () => useChat.getState().setStatus('streaming'),
        },
      });

      useChat.getState().endTurn();
      setTurnKind(null);
      {
        const anchorId = lastBubbleOfTurn(turn);
        useActivity.getState().finish(turn, { anchorId, threadId: useSession.getState().threadId });
      }

      if (outcome.status === 'error') {
        // A report run failing is not the visitor's fault — there is nothing
        // to retry (they typed nothing) and no composer to put text back
        // into. `pending_reports` (MR 1's `on_turn_start`) is the fallback:
        // the next ordinary turn still folds the brief in.
        // eslint-disable-next-line no-console
        console.error('[live-report]', delegationId, outcome.error);
      }

      // A message queued while this report ran wins over chaining into the
      // next deferred report — same precedence as `dispatch`'s own tail, and
      // for the same reason (a queued visitor message, `chat/sendQueue.ts`).
      // Otherwise, a report finishing can itself unblock another one that was
      // deferred behind it (two jobs finishing close together, the chat idle
      // both times).
      const queued = dequeueSend(sendQueue);
      if (queued) {
        void dispatch(queued.text, { bubbleId: queued.bubbleId, tap: queued.tap });
      } else {
        const next = schedulerOnEndTurn(reportScheduler, useChat.getState().draft.trim() === '');
        for (const id of next) void runReportTurn(id);
      }
    },
    [applyWorkerEvent],
  );

  const scheduleReport = useCallback(
    (delegationId: string) => {
      const decision = onWorkerDone(
        reportScheduler,
        delegationId,
        useChat.getState().status === 'idle',
        useChat.getState().draft.trim() === '',
      );
      if (decision.action === 'start') void runReportTurn(delegationId);
    },
    [runReportTurn],
  );

  /**
   * The RETRY for a deferred report — the bug this fixes, live on dev
   * 2026-09-16 (team-lead's report): `Composer.tsx`'s textarea is never
   * disabled while a turn streams (only the send/stop button swaps), so a
   * visitor typing their next question AHEAD of the answer makes `draft`
   * non-empty at the exact instant `endTurn()` fires. `schedulerOnEndTurn`
   * (in `dispatch`/`runReportTurn`, right after `endTurn()`) correctly defers
   * again in that case — and until this effect, NOTHING ever rechecked once
   * the composer went back to empty (deleted, or never sent): the report sat
   * in `pending` until the visitor's own next `dispatch()` wiped it via
   * `cancelDeferred`, which is exactly the observed defect — two jobs
   * finished while their turn was still streaming, the turn ended with a
   * non-empty composer, and the report never fired until the NEXT ordinary
   * turn folded the brief in instead (`pending_reports`, MR 1's safety net).
   *
   * `onEndTurn` is idempotent to call again with nothing pending (a
   * `pending.size === 0` no-op), so this runs on every transition into
   * «idle + empty composer» without needing to know whether the single
   * `endTurn()`-site call already handled it.
   */
  const composerIdle = chat.status === 'idle' && chat.draft.trim() === '';
  useEffect(() => {
    if (!composerIdle) return;
    const ready = schedulerOnEndTurn(reportScheduler, true);
    for (const id of ready) void runReportTurn(id);
  }, [composerIdle, runReportTurn]);

  // ── one turn ───────────────────────────────────────────────────────────
  /**
   * Runs one turn against the backend. `dispatch` itself always REQUIRES an
   * idle chat — `enqueueOrSend` below is the one caller, and it is
   * already idle when it calls this, or it is `dispatch`'s
   * own tail / `runReportTurn`'s own tail / `stop()` re-dispatching a message
   * that waited in the queue (`chat/sendQueue.ts`) while a report ran.
   *
   * `opts.tap` is the board tap this message carries — ALWAYS supplied by the
   * caller (`enqueueOrSend` reads `takeLastTap()` exactly once, synchronously
   * with the tap that triggered the call; `dispatch` itself never calls it,
   * or a queued message's tap — read once already — would come back `null`
   * here). `opts.bubbleId` is set only for a message already pushed to the
   * thread while it waited in the queue — `markUserSent` clears its pending
   * mark and restamps it onto THIS turn instead of `pushUser` pushing a
   * second bubble for the same message.
   */
  const dispatch = useCallback(
    async (text: string, opts: { bubbleId?: string; tap: Record<string, unknown> | null }) => {
      const trimmed = text.trim();
      if (!trimmed) return;
      if (useChat.getState().status !== 'idle') return;

      // `peterhof_live`: the visitor is sending a message, so any report still
      // waiting for the chat to go idle is dropped — the backend folds that
      // job's brief into the turn about to start instead
      // (`chat/reportScheduler.ts`'s own docstring).
      cancelDeferred(reportScheduler);

      // The turn starts NOW — the visitor's line is on the screen and the guide «thinks» — and the browser's position,
      // if the page may have it, catches up into the context before the request goes out (owner, QA 24.10.2026:
      // «геопозиция разрешена, а гид не знает, где я»). A second send meanwhile is the usual «a turn is running»:
      // ignored, the draft kept (INV-PH-137). Never asks for permission, ~4 s at most — `location/autoLocate.ts`.
      const started = useSession.getState();
      const { threadId, assistantId } = started;
      const tap = opts.tap;
      const permissions = typeof navigator === 'undefined' ? undefined : navigator.permissions;
      const geolocation = typeof navigator === 'undefined' ? undefined : navigator.geolocation;
      const waitsForPosition = !!permissions && !!geolocation && wantsAutoGps(started, Date.now());
      const controller = new AbortController();
      abort.current = controller;
      setTurnKind('visitor');

      useChat.getState().beginTurn(assistantId);
      const turn = useChat.getState().turn;
      useActivity.getState().begin(turn);
      if (opts.bubbleId !== undefined) {
        // Already on screen (queued while a report ran) — clear its pending
        // mark and stamp it onto THIS turn, never a second bubble for one
        // message.
        useChat.getState().markUserSent(opts.bubbleId);
      } else {
        // A navigation event is the visitor's line on the wire and a PILL in
        // the chat: it is something that happened to them, not something
        // they typed. Read off the event NAME, never off the Russian
        // sentence (§4.3).
        useChat.getState().pushUser(trimmed, isNavEvent(tap?.name) ? 'nav' : undefined);
      }
      if (waitsForPosition) {
        await autoLocate({
          session: () => useSession.getState(),
          permissions: permissions!,
          geolocation: geolocation!,
          setAgentGeo: (point, meta) => useSession.getState().setAgentGeo(point, meta),
          touchAgentGeo: (meta) => useSession.getState().touchAgentGeo(meta),
          reverse: reverseGeocode,
        });
        // «Стоп» or a new thread while the browser answered: that already ended this turn, nothing is sent
        if (controller.signal.aborted || useSession.getState().threadId !== threadId) return;
      }

      const { userId, geo, agentGeo, agentGeoSet, agentGeoMeta, locationResetAt, agentLocationRevision } = useSession.getState();
      const boardIsTheGraphs = speaksA2ui(assistantId);
      const model = currentModel(); // undefined = no open-model override chosen
      // The one field two switches write to — the orchestrator override wins
      // on the two orch graphs, `model` (above) everywhere else. See
      // `sessionStore.currentContextModel`'s own docstring for why: reagent
      // 400s on `config.configurable` beside `context`, so both switches ride
      // `context.model` and this is where they are reconciled.
      const contextModel = currentContextModel();
      // …and the BACKGROUND model rides its own field: `peterhof_tandem` runs
      // two models at once, and `context.model` is already the fast contour's.
      const workerModel = currentWorkerModel();
      // The effort of the pinned model ROW, one per contour («GPT-5.6 Terra ·
      // low» — `sessionStore.withReasoning`) — `undefined` when nothing is
      // pinned or the model has no effort, which leaves the deploy's own.
      const reasoningEffort = currentReasoningEffort();
      const workerReasoningEffort = currentWorkerReasoningEffort();
      const requestContext = visitorContext({
        userId,
        assistantId,
        contextModel,
        workerModel,
        reasoningEffort,
        workerReasoningEffort,
        temperature: model?.temperature,
        geo,
        agentGeo,
        agentGeoSet,
        geoMeta: agentGeoMeta,
        locationResetAt,
        tap,
      });
      const requestOrigin: NavigationOrigin | null =
        requestContext.latitude !== undefined && requestContext.longitude !== undefined
          ? { lat: requestContext.latitude, lon: requestContext.longitude, source: requestContext.location_source ?? 'gps' }
          : null;
      const requestLocationAt = requestContext.location_at ?? null;
      // Set when this turn's pinned model was dropped (#140) — see the queue below.
      let pinDropped = false;
      const locationRequest: LocationRequest = { threadId, turn, origin: requestOrigin, locationAt: requestLocationAt, revision: agentLocationRevision, resetAt: locationResetAt };
      // The surface persists across turns — it is the trip, not the turn.

      const outcome = await runTurn({
        threadId,
        assistantId,
        text: trimmed,
        streamMode: streamModeFor(assistantId),
        signal: controller.signal,
        context: requestContext,
        handlers: {
          onEvent: (event) => {
            useChat.getState().ingest(event);
            if (event.kind === 'run_error') {
              // A pinned model the upstream refuses (#140): drop the pin and
              // say so — the utterance goes back into the composer, and the
              // next turn runs on the deploy's model. No automatic retry.
              const dropped = dropDeadModelPin(contextModel, event.error, event.message);
              if (dropped) {
                pinDropped = true;
                useChat.getState().fail({ text: deadModelText(dropped), retryText: trimmed });
              }
            } else if (event.kind === 'a2ui') {
              applyFrame(surfaceProcessor, event.frame, frameGate.current);
            } else if (event.kind === 'activity') {
              const frame = readActivityFrame(event.payload);
              if (frame) useActivity.getState().apply(frame, turn);
            } else if (event.kind === 'reasoning') {
              const frame = readReasoningFrame(event.payload);
              if (frame) useActivity.getState().applyReasoning(frame, turn);
            } else if (event.kind === 'navigation' || event.kind === 'location_selection') {
              applyNavigationEvent(event.kind, event.payload, locationRequest);
            } else if (event.kind === 'values' && !boardIsTheGraphs && !hasNoBoard(assistantId)) {
              // Compatibility path, and ONLY for the older graphs that have no
              // board of their own: `?graph=peterhof_guide` builds one from
              // `attachments` (§3.4). The canvas graph ships both — its A2UI
              // frames AND the Telegram attachments — so drawing these here put
              // the afisha on the board twice. The NEW `peterhof_agent`
              // (18.09) is excluded too — `hasNoBoard` — because it has no
              // board to build one FOR: its state carries no `attachments`
              // channel, and `TurnCards` is its only surface for a plan.
              applyAttachments(event.values.attachments);
            } else if (event.kind === 'interrupt') {
              // `peterhof_agent`'s questionnaire tool pauses the run with a
              // full form rather than a call-and-response — §plan
              // «Interrupt/resume». An unrecognised `value.kind` is left for a
              // future scenario type to define; this build only knows
              // "questionnaire".
              const first = event.interrupts[0];
              const value = first ? readQuestionnaireValue(first.value) : null;
              if (first && value) {
                useQuestionnaire.getState().open(first.id, value);
              } else if (first) {
                // eslint-disable-next-line no-console
                console.warn('[interrupt] unrecognised value, ignoring', first.value);
              }
            } else if (event.kind === 'worker_started') {
              // `peterhof_orch`/`peterhof_tandem`: a delegation started on its
              // own thread. `turn` is THIS turn's own — captured above, before
              // any frame arrived — so the worker's steps land in the feed of
              // the turn that spawned it even after `endTurn()` has already
              // fired («не должен ломать … статус основного хода», so this
              // never touches `useChat`'s status itself).
              useWorkerStream
                .getState()
                .start(threadId, { sideThreadId: event.threadId, runId: event.runId }, (workerEvent) =>
                  applyWorkerEvent(workerEvent, turn),
                );
            } else if (event.kind === 'worker_done') {
              // Defensive: the contract allows this frame on the MAIN stream
              // too, though the ordinary case is the worker's own side stream
              // (`applyWorkerEvent` above).
              scheduleReport(event.delegationId);
            }
            // `event.kind === 'progress'` needs no branch here beyond the
            // unconditional `ingest(event)` above — same as every other graph's
            // ordinary message frames.
          },
          onReconnect: () => useChat.getState().setStatus('reconnecting'),
          onRecovered: () => useChat.getState().setStatus('streaming'),
        },
      });

      // This turn may have been STOPPED («Стоп», `stop()`): that ends the turn
      // at once and, with a message queued behind a report run, dispatches it
      // without waiting for this promise to settle. (A typed message no longer
      // interrupts a turn — owner, 2026-10-18, INV-PH-137.) By the time it
      // does, `abort.current` already belongs to that NEWER turn's own
      // controller — so a stale continuation must never
      // touch anything the newer turn owns: `chatStore`'s shared `status`
      // (`endTurn()` would flip it back to idle mid-stream, and un-stream the
      // newer turn's own bubble), the send queue, the report scheduler, or an
      // error banner for a cancellation nobody needs telling about. It still
      // closes ITS OWN activity feed below (keyed by `turn`, not shared, and
      // its bubble's `streaming` flag was already cleared synchronously by
      // `interruptCurrentTurn`'s own `endTurn()` call) — or the stopped
      // turn's spinner would never stop.
      const superseded = abort.current !== controller;
      if (!superseded) {
        abort.current = null;
        useChat.getState().endTurn();
        setTurnKind(null);
        // A message queued while THIS turn was running wins over everything
        // else the chat going idle could do next — it is dispatched at once,
        // ahead of a deferred report (`dispatch` itself drops any pending one
        // via `cancelDeferred` at its own top). With nothing queued, a report
        // deferred while this turn ran may now start — unless the visitor is
        // already typing the next one (`schedulerOnEndTurn`'s own
        // `composerIsEmpty` check), in which case it waits for the NEXT
        // `endTurn`.
        //
        // Not after a dropped model pin (#140): the next turn's `beginTurn`
        // would clear the error that says why this one failed, and with it the
        // utterance put back for a retry. The queue waits for the visitor's own
        // next send, whose `endTurn` flushes it.
        const queued = pinDropped ? undefined : dequeueSend(sendQueue);
        if (queued) {
          void dispatch(queued.text, { bubbleId: queued.bubbleId, tap: queued.tap });
        } else if (!pinDropped) {
          const ready = schedulerOnEndTurn(reportScheduler, useChat.getState().draft.trim() === '');
          for (const id of ready) void runReportTurn(id);
        }
      }
      // The spinner gates on ENDING THE TURN, not on the backend's own
      // `finished` frame (§2.11) — a killed container or a dropped stream
      // must still close the feed.
      {
        const anchorId = lastBubbleOfTurn(turn);
        useActivity.getState().finish(turn, { anchorId, threadId: useSession.getState().threadId });
        useNavigation.getState().remember(turn, anchorId, useSession.getState().threadId);
      }

      // A stale (interrupted) continuation shows nothing at all here — its
      // outcome is `'cancelled'` in the ordinary case, and even an `'error'`
      // or `'unsupported'` from an aborted request is not news the visitor,
      // now reading a DIFFERENT turn's answer, needs a banner about.
      if (!superseded) {
        if (outcome.status === 'error') {
          const error = outcome.error;
          if (error instanceof ApiError) {
            // eslint-disable-next-line no-console
            console.error('[run]', error.status, error.code, error.message);
            if (error.status === 429) {
              useChat.getState().fail({
                text: `Слишком много запросов. Подождите ${error.retryAfter ?? 30} секунд.`,
                retryText: trimmed,
                retryAfterSeconds: error.retryAfter ?? 30,
              });
            } else if (error.status === 404) {
              // The thread is gone (a wiped database, an expired run). We say so
              // in one line rather than pretending the history is intact.
              useSession.getState().newThread();
              useChat.getState().reset();
              useSurface.getState().reset();
              useActivity.getState().reset();
              useChat.getState().fail({ text: 'Начали новый диалог.', retryText: trimmed });
            } else {
              useChat.getState().fail({
                text: 'Гид сейчас не отвечает. Попробуем ещё раз?',
                retryText: trimmed,
              });
            }
          } else if (error.message === 'stream-lost') {
            useChat
              .getState()
              .fail({ text: 'Связь оборвалась и не восстановилась.', retryText: trimmed });
          } else {
            useChat
              .getState()
              .fail({ text: 'Гид сейчас не отвечает. Попробуем ещё раз?', retryText: trimmed });
          }
        } else if (outcome.status === 'unsupported') {
          useChat.getState().fail({
            text: 'Этот браузер не умеет читать ответ по частям — обновите его, пожалуйста.',
          });
        }
      }
    },
    [],
  );

  /**
   * Answers a paused questionnaire — the SAME run, resumed with `command:
   * {resume}` in place of `input` (§plan «Interrupt/resume»). Structurally a
   * smaller `dispatch`: one `beginTurn`/`endTurn` cycle, the same frame
   * handlers, so anything the graph draws after resuming (a card, a chip)
   * reaches the board through the ordinary path. Deliberately NOT sharing
   * `dispatch`'s `AbortController` — the same reason `runReportTurn` does
   * not — and it does not consult `useChat.getState().status` the way
   * `dispatch` does either: the only two callers (`QuestionCard`'s taps and
   * `enqueueOrSend`'s exit path) both run once the interrupted run has
   * already ended, which is what makes the questionnaire visible at all.
   *
   * `visitorBubbleText`, when given, is pushed as an ordinary user bubble
   * BEFORE the request goes out — the completed questionnaire's own summary
   * («Когда: в выходные · …») or the typed sentence that exited it — so the
   * transcript reads as a normal exchange rather than a resume nobody said
   * anything about.
   */
  const submitResume = useCallback(async (resume: ResumeValue, visitorBubbleText?: string) => {
    if (restoredThread.current !== useSession.getState().threadId) return;
    if (useChat.getState().status !== 'idle') return;
    const { threadId, assistantId, userId, geo, agentGeo, agentGeoSet, agentGeoMeta, locationResetAt, agentLocationRevision } = useSession.getState();
    const model = currentModel();
    const contextModel = currentContextModel();
    const workerModel = currentWorkerModel();
    const reasoningEffort = currentReasoningEffort();
    const workerReasoningEffort = currentWorkerReasoningEffort();
    const controller = new AbortController();
    abort.current = controller;
    setTurnKind('visitor');

    useChat.getState().beginTurn(assistantId);
    const turn = useChat.getState().turn;
    useActivity.getState().begin(turn);
    if (visitorBubbleText) useChat.getState().pushUser(visitorBubbleText);

    const requestContext = {
      user_id: userId,
      channel: 'web' as const,
      ...(contextModel ? { model: contextModel } : {}),
      ...(workerModel ? { worker_model_override: workerModel } : {}),
      ...(reasoningEffort ? { reasoning_effort: reasoningEffort } : {}),
      ...(workerReasoningEffort ? { worker_reasoning_effort: workerReasoningEffort } : {}),
      ...(model && model.temperature !== undefined ? { temperature: model.temperature } : {}),
      ...locationContext(assistantId, geo, agentGeoMeta, agentGeo, agentGeoSet, locationResetAt),
    };
    const requestOrigin: NavigationOrigin | null =
      requestContext.latitude !== undefined && requestContext.longitude !== undefined
        ? { lat: requestContext.latitude, lon: requestContext.longitude, source: requestContext.location_source ?? 'gps' }
        : null;
    const requestLocationAt = requestContext.location_at ?? null;
    const locationRequest: LocationRequest = { threadId, turn, origin: requestOrigin, locationAt: requestLocationAt, revision: agentLocationRevision, resetAt: locationResetAt };

    const outcome = await runTurn({
      threadId,
      assistantId,
      text: '',
      command: { resume },
      streamMode: streamModeFor(assistantId),
      signal: controller.signal,
      context: requestContext,
      handlers: {
        onEvent: (event) => {
          useChat.getState().ingest(event);
          if (event.kind === 'run_error') {
            // The same rule as `dispatch` (#140); a resume has no utterance to put back.
            const dropped = dropDeadModelPin(contextModel, event.error, event.message);
            if (dropped) useChat.getState().fail({ text: deadModelText(dropped) });
          } else if (event.kind === 'a2ui') {
            applyFrame(surfaceProcessor, event.frame, frameGate.current);
          } else if (event.kind === 'activity') {
            const frame = readActivityFrame(event.payload);
            if (frame) useActivity.getState().apply(frame, turn);
          } else if (event.kind === 'reasoning') {
            const frame = readReasoningFrame(event.payload);
            if (frame) useActivity.getState().applyReasoning(frame, turn);
          } else if (event.kind === 'navigation' || event.kind === 'location_selection') {
            applyNavigationEvent(event.kind, event.payload, locationRequest);
          } else if (event.kind === 'interrupt') {
            // Defensive completeness: today's one scenario asks its whole form
            // in a single `interrupt()`, so a second one back to back is not
            // part of the contract — handled the same way as the first rather
            // than dropped.
            const first = event.interrupts[0];
            const value = first ? readQuestionnaireValue(first.value) : null;
            if (first && value) useQuestionnaire.getState().open(first.id, value);
          }
        },
        onReconnect: () => useChat.getState().setStatus('reconnecting'),
        onRecovered: () => useChat.getState().setStatus('streaming'),
      },
    });

    abort.current = null;
    useChat.getState().endTurn();
    setTurnKind(null);
    {
      const anchorId = lastBubbleOfTurn(turn);
      useActivity.getState().finish(turn, { anchorId, threadId: useSession.getState().threadId });
      useNavigation.getState().remember(turn, anchorId, useSession.getState().threadId);
    }

    if (outcome.status === 'error') {
      const error = outcome.error;
      // eslint-disable-next-line no-console
      console.error('[resume]', error instanceof ApiError ? error.status : error);
      useChat.getState().fail({ text: 'Гид сейчас не отвечает. Попробуем ещё раз?' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * «Стоп», the mechanism: abort the streaming request, tell the SERVER to
   * cancel it, and close the turn on the client — shared by the visible
   * button (`stop`, below). (It used to be shared with `enqueueOrSend`'s own
   * interrupt path — owner's «перебивание», 2026-09-16 — cancelled 2026-10-18,
   * INV-PH-137.) It never touches the send queue
   * or the report scheduler itself — callers decide what happens next.
   */
  const interruptCurrentTurn = useCallback(() => {
    const { runId } = useChat.getState();
    const { threadId } = useSession.getState();
    abort.current?.abort();
    abort.current = null;
    // Stopping ends the RENDERING and the run — it never touches the thread or
    // the board (UI_CONTRACT §4.3).
    if (runId) void cancelTurn(threadId, runId);
    useChat.getState().endTurn();
    setTurnKind(null);
  }, []);

  /**
   * The ONE entry point every sender (`lib/send.ts`'s `wireSender`, and
   * `Composer.tsx`'s own submit) reaches. `chat/sendQueue.ts`'s `decideSend`
   * makes the call (owner, 2026-10-18, INV-PH-137 — it cancels the 2026-09-16
   * «перебивание»): an idle chat dispatches at once; a streaming
   * VISITOR turn IGNORES the message (`false`, nothing queued, the turn goes
   * on — the visitor stops it with «Стоп» if they want); a streaming `peterhof_live`
   * REPORT run is QUEUED instead, because its result would be lost for good
   * (the backend marks the job reported the moment the run starts) — the
   * visitor's bubble goes up right now (pending), and `dispatch`'s own tail
   * sends it the moment the report ends. `takeLastTap()` is read HERE,
   * synchronously with the tap that triggered this call (`dispatchTap` calls
   * `send()` straight after setting it) — reading it later, at the moment a
   * queued or re-dispatched message actually goes out, would see `null` or a
   * different tap.
   *
   * → whether the message was TAKEN (`false`: offline, another thread, empty) — what lets a
   * button that answers a press at once («Убираю…», `catalog/planRemoval.ts`) not do it for a
   * press that went nowhere.
   */
  const enqueueOrSend = useCallback(
    (text: string): boolean => {
      // Consume the one-shot context even when this submission is rejected.
      const tap = takeLastTap();
      if (offline || restoredThread.current !== useSession.getState().threadId) return false;
      const trimmed = text.trim();
      if (!trimmed) return false;
      // A questionnaire is active only once its `interrupt()`ed run has
      // already ended (`useChat`'s status is idle by the time the card is on
      // screen), so this branch never races `dispatch`/`submitResume`'s own
      // idle check. Free text typed HERE — not a tap inside `QuestionCard`
      // itself — is the contract's `resume {status:"exited", text}`: an
      // ordinary input-run on the paused thread is never created.
      if (useQuestionnaire.getState().active) {
        const card = useQuestionnaire.getState().active;
        useQuestionnaire.getState().close();
        void submitResume(exitedResume(trimmed, card?.answers, card?.skipped), trimmed);
        return true;
      }
      const decision = decideSend(useChat.getState().status, useTurnKind.getState().kind);
      if (decision === 'dispatch') {
        void dispatch(trimmed, { tap });
        return true;
      }
      // The visitor's own turn is streaming: owner's 2026-10-18 rule (INV-PH-137)
      // — nothing is sent while the guide answers. Not taken, so the caller keeps
      // its draft / does not mark a button as pressed; the turn is not touched.
      if (decision === 'ignore') return false;
      // 'queue'
      const kind = isNavEvent(tap?.name) ? 'nav' : undefined;
      const bubbleId = useChat.getState().pushUser(trimmed, kind, true);
      enqueueSend(sendQueue, { text: trimmed, bubbleId, tap });
      return true;
    },
    [dispatch, interruptCurrentTurn, submitResume, offline],
  );

  useEffect(() => wireSender((text) => enqueueOrSend(text)), [enqueueOrSend]);

  const stop = useCallback(() => {
    interruptCurrentTurn();
    // «Стоп» cancels the turn, not a message the visitor already queued
    // behind it (only possible here if a report run was cancelled mid-flight)
    // — that message is sent now, exactly as if the turn had finished on its
    // own.
    const queued = dequeueSend(sendQueue);
    if (queued) void dispatch(queued.text, { bubbleId: queued.bubbleId, tap: queued.tap });
  }, [dispatch, interruptCurrentTurn]);

  const newThread = useCallback(() => {
    abort.current?.abort();
    abort.current = null;
    setTurnKind(null);
    useSession.getState().newThread();
    useChat.getState().reset();
    useSurface.getState().reset();
    useActivity.getState().reset();
    useNav.getState().reset();
    useBoardPanel.getState().reset();
    useQuestionnaire.getState().close();
    // A new thread means a new worker if there ever is one — «🆕 Новый
    // диалог» must not leave the OLD thread's side connection running
    // against a board that has just been wiped.
    useWorkerStream.getState().reset();
    frameGate.current.lastSeq = 0;
    workerFrameGate.current.lastSeq = 0;
    resetReportScheduler(reportScheduler);
    resetSendQueue(sendQueue);
    setPane('chat');
  }, []);

  // ── coming back to a thread ────────────────────────────────────────────
  // No submissions until this thread's initial read settles. An unreached read
  // raises the curtain; recovery reload waits for turns, queued input and any
  // draft that tab storage could not preserve.
  const restoreFailed = useRef(false);
  const recoveryPending = useRef(false);
  useEffect(() => {
    const draft = readDraft();
    if (draft !== null) useChat.getState().setDraft(draft);
    return useChat.subscribe((state, previous) => {
      if (state.draft !== previous.draft) saveDraft(state.draft);
    });
  }, []);

  useEffect(() => {
    restoredThread.current = null;
    restoreFailed.current = false;
    recoveryPending.current = false;
    const threadId = session.threadId;
    // Synchronous, and read first: `noticeContent()` below (fired by the
    // `hasBoardContent` effect once the restored board lands) must see
    // whether THIS thread was closed by hand before it decides whether to
    // auto-open the panel — restoring the sticky flag is what keeps a
    // reload from re-opening a panel the visitor just put away (§11.3).
    useBoardPanel.getState().restore(session.threadId);
    let cancelled = false;
    void (async () => {
      try {
        const state = await getThreadState(useSession.getState().threadId);
        if (cancelled) return;
        useChat.getState().restore(state.values.messages);
        // A ✕ pressed before the reload whose answer is still outstanding stays held (INV-PH-114).
        void resumeRemoval(threadId, useChat.getState().bubbles);
        // Collapsed summaries only (F5, §2.3/§5.2) — the live feed never
        // survives a reload, and this restores what does: `ph.activity.<id>`
        // in localStorage, anchored on the bubble ids `restore()` just kept.
        useActivity.getState().restore(useSession.getState().threadId);
        // …and the map card under the answer it belonged to (B13): custom SSE only, so the browser's copy
        // (`location/navigationHistory.ts`) is the only source. A live frame already on screen wins.
        useNavigation.getState().restore(
          useSession.getState().threadId,
          (id) => useChat.getState().bubbles.some((bubble) => bubble.id === id),
        );
        // F5 / a thread reopened: `values.board.surface` is ALREADY an A2UI
        // document, so the client wraps it in a `createSurface` literal (§2.1)
        // rather than translating anything. Where there is no board — the older
        // graphs — the attachments shim builds one.
        const created = createSurfaceFromState(state.values, useSession.getState().threadId);
        if (created) useSurface.getState().applyCreateSurface(created.message, created.rev);
        // A canvas thread's board is its board document or nothing: an older
        // one whose stored shape we cannot read must not be re-drawn out of
        // attachments the graph meant for Telegram. `peterhof_agent` (18.09)
        // has no board to build one for either — `hasNoBoard`.
        else if (!speaksA2ui(useSession.getState().assistantId) && !hasNoBoard(useSession.getState().assistantId))
          applyAttachments(state.values.attachments);
        useSurface.getState().markSeen();
        // A questionnaire paused mid-turn survives the reload only here: the
        // event log a live turn streamed `interrupt` frames through is gone,
        // and the checkpoint's `interrupts` array is the one place left that
        // still says so (§plan «Interrupt/resume», restoration).
        const interrupt = state.interrupts[0];
        if (interrupt) {
          const value = readQuestionnaireValue(interrupt.value);
          if (value) useQuestionnaire.getState().open(interrupt.id, value);
          else
            // eslint-disable-next-line no-console
            console.warn('[interrupt] unrecognised value on restore, ignoring', interrupt.value);
        }
        // F5 mid-delegation: `/state` carries no record of the worker's own
        // cards yet (§workerStream.ts's own doc — a stored `board` is the
        // MAIN thread's), so the only way back to them is replaying the
        // worker's own stream from the start. A no-op when this thread never
        // had one. `useChat.getState().turn` is read PER FRAME rather than
        // captured once: right after a reload it is the restored default (0,
        // `chatStore.restore()` never touches it) until the visitor's first
        // live turn moves it — reading it fresh is what keeps a worker frame
        // landing in whichever feed is actually current.
        useWorkerStream
          .getState()
          .reconnect(useSession.getState().threadId, (event) =>
            applyWorkerEvent(event, useChat.getState().turn),
          );
      } catch (error) {
        // A thread with nothing in it is the ordinary first visit, not a fault.
        // One the guide never answered for is retried when the curtain lifts.
        if (!cancelled && restoreUnreached(error)) {
          restoreFailed.current = true;
          setOffline(true);
        }
      } finally {
        if (!cancelled) {
          restoredThread.current = threadId;
          setRestoredThreadId(threadId);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // Runs once per thread id: «Новый диалог» makes a new one and there is
    // nothing to restore.
  }, [session.threadId]);

  // ── a saved plan's link (INV-PH-166) ────────────────────────────────────
  // `?trip=` in the address is the token on a PDF's or a calendar entry's link. It is taken out of the address at
  // once (`openedTripToken`), and sent as an ACTION — `context.trip_link` — once this thread's read has settled
  // (the same gate every send waits at) and the network is up. The graph's code rebuilds the plan and greets; on
  // a thread that has a conversation the plan lands in it, as a "С возвращением…" turn. A graph that cannot read
  // the token is never sent the line (`readsTripLink`; the link itself names `graph=peterhof_v2`).
  const tripToken = useRef(openedTripToken());
  useEffect(() => {
    if (!tripToken.current || offline || restoredThreadId !== session.threadId) return;
    if (!readsTripLink(session.assistantId)) return;
    const token = consumeTripToken();
    if (!token) return;
    if (!dispatchTripLink(RETURN_LINE, token)) unconsumeTripToken();
  }, [offline, restoredThreadId, session.threadId, session.assistantId]);

  // ── what the deploy runs by default ────────────────────────────────────
  // Once per page load: the settings menu's checkmarks on a FRESH conversation
  // come from here, before the thread's first `run_meta` exists
  // (`sessionStore.orchChoiceIdFor` & co.). A missing file (the public build)
  // resolves to `{}` and changes nothing.
  useEffect(() => {
    const controller = new AbortController();
    void deployModels(controller.signal).then((models) => useSession.getState().setDeployModels(models));
    return () => controller.abort();
  }, []);

  // ── the curtain, and finding our way back from behind it ───────────────
  // The browser's own network events (#43): losing the network on the territory
  // shows the offline snapshot at once; regaining it asks the guide, not the event.
  useEffect(() => {
    const down = () => setOffline(true);
    const up = () => void health().then((ok) => setOffline(!ok));
    window.addEventListener('offline', down);
    window.addEventListener('online', up);
    return () => {
      window.removeEventListener('offline', down);
      window.removeEventListener('online', up);
    };
  }, []);

  const wasOffline = useRef(offline);
  const queuedInput = chat.bubbles.some((bubble) => bubble.pending);
  useEffect(() => {
    if (wasOffline.current && !offline && restoreFailed.current) recoveryPending.current = true;
    wasOffline.current = offline;
    // Read the live status: an earlier effect or the turn's tail can already
    // have started the next queued turn since this render's snapshot.
    const currentChat = useChat.getState();
    const queued = hasQueued(sendQueue) || currentChat.bubbles.some((bubble) => bubble.pending);
    if (retryRestore(recoveryPending.current, offline, restoreFailed.current, currentChat.status, queued)) {
      // A blocked/full tab storage leaves the only copy here. Keep recovery
      // pending until the visitor sends or clears it, then wait for that turn.
      if (!saveDraft(currentChat.draft) && currentChat.draft !== '') return;
      recoveryPending.current = false;
      window.location.reload();
    }
  }, [offline, chat.status, chat.draft, queuedInput]);

  useEffect(() => {
    if (!offline) return;
    const timer = setInterval(() => {
      void health().then((ok) => {
        if (ok) setOffline(false);
      });
    }, 15000);
    return () => clearInterval(timer);
  }, [offline]);

  useEffect(() => {
    const error = chat.error;
    if (!error) return;
    if (error.text.startsWith('Гид сейчас не отвечает')) {
      void health().then((ok) => setOffline(!ok));
    }
  }, [chat.error]);

  // ── «the board updated» ────────────────────────────────────────────────
  const unseen = surface.unseen;
  const panelOpen = useBoardPanel((state) => state.open);
  const panelEverAppeared = useBoardPanel((state) => state.everAppeared);
  useEffect(() => {
    if (!announcesBoardUpdate({ noBoard, wide, pane, unseen })) return;
    setToast('Доска обновилась');
    const timer = setTimeout(() => setToast(null), 4000);
    return () => clearTimeout(timer);
  }, [unseen, wide, pane, noBoard]);

  // Visible means: the mobile board tab is the one on screen, or — on wide —
  // the panel is actually open. A wide layout used to mark everything seen
  // unconditionally (the panel was always on screen); now a collapsed panel
  // must keep counting, or the header's own badge would always read zero.
  useEffect(() => {
    if ((wide && panelOpen) || (!wide && pane === 'board')) useSurface.getState().markSeen();
  }, [wide, panelOpen, pane, unseen]);

  // The panel does not exist until the board has something to show (§11.3) —
  // `noticeContent()` is idempotent past its first call, so calling it again
  // on every later card costs nothing.
  const hasBoardContent =
    surface.regionNodes('main').length > 0 || surface.regionNodes('side').length > 0;
  useEffect(() => {
    if (hasBoardContent) useBoardPanel.getState().noticeContent();
  }, [hasBoardContent]);

  // «Открыть на карте» under an answer (`chat/TurnCards.tsx`). The chat card
  // only names what to show; WHERE the board is on screen is this component's
  // knowledge: the «Доска» tab below the breakpoint, the panel above it (opened
  // if the visitor had collapsed it — they are asking to see it now). The
  // scroll waits one short beat, because the card is in the DOM only once its
  // pane is (`focus.ts` reads the DOM on purpose).
  const focusRequest = useBoardPanel((state) => state.focusRequest);
  useEffect(() => {
    if (!focusRequest) return;
    if (wide) useBoardPanel.getState().reveal(useSession.getState().threadId);
    else setPane('board');
    const timer = setTimeout(() => focusBoardCard(focusRequest.id), 80);
    return () => clearTimeout(timer);
    // `wide` is read at the moment of the tap, not tracked: a resize after it
    // is not a second request.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusRequest]);

  // `inert` is only meaningful on wide layouts, where the panel can be present
  // but collapsed — mobile never collapses its board pane, it hides it with
  // `hidden`, which already removes it from both the tree and the tab order.
  // `panelEverAppeared` is in the deps because the section is not mounted
  // (`boardSection.current` is `null`) until it flips true, and neither `wide`
  // nor `panelOpen` is guaranteed to change value on that same render.
  useEffect(() => {
    const el = boardSection.current;
    if (!el) return;
    // Never in the navigation mode: the map is the screen there, and the
    // panel flag is not what decides whether it may be reached.
    (el as HTMLElement & { inert: boolean }).inert = wide && !panelOpen && !navActive;
  }, [wide, panelOpen, panelEverAppeared, navActive]);

  // ── the emergency note ─────────────────────────────────────────────────
  const emergency = useMemo(() => {
    // `region:header` is where the graph puts it, and this is the pin that makes
    // it visible on the chat tab too — the one hard layout commitment (§4.5).
    for (const node of useSurface.getState().regionNodes('header')) {
      if (node.component !== 'NoteCard') continue;
      const props = node.props as NoteCardProps;
      if (props?.tone === 'emergency') return props;
    }
    return null;
  }, [surface.components, surface.exiting]);

  if (offline) {
    return (
      <div className="app">
        <Header wide={wide && !navActive} onNewThread={newThread} onSend={(text) => enqueueOrSend(text)} />
        <div className="curtain">
          <h2 className="curtain__title">Гид временно недоступен</h2>
          <p className="curtain__text">
            Страница на месте, а связь с гидом сейчас потеряна. Проверяю каждые 15 секунд и вернусь
            сам, как только он ответит.
          </p>
          <button
            type="button"
            className="btn btn--primary"
            onClick={() => void health().then((ok) => setOffline(!ok))}
          >
            Проверить сейчас
          </button>
          <OfflineGuide />
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        'app',
        navActive && 'app--nav',
        navActive && navSheet === 'expanded' && 'app--sheet-open',
      )}
    >
      {/* `wide` is Header's question «is the two-column layout in play», and
          in the navigation mode it is not: there is one screen and it is the
          map. Passing the raw breakpoint would ship a «Свернуть доску» toggle
          that does nothing — nav counts the panel as open whatever the flag
          says — and leave it in the tab order saying so. */}
      <Header
        wide={wide && !navActive}
        onNewThread={newThread}
        onSend={(text) => enqueueOrSend(text)}
      />

      {emergency ? (
        // Above everything, on both panes, with no entrance animation: this is
        // the one card where «did not see it» is the expensive outcome.
        <div className="emergency-slot">
          <ErrorBoundary label="emergency">
            <NoteCard props={emergency} />
          </ErrorBoundary>
        </div>
      ) : null}

      {/* The segmented control is a choice between two panes, and in the
          navigation mode there is one screen: the board IS the map (§2.2).
          `peterhof_agent` (`noBoard`) has only the chat pane at all — a
          two-item segmented control offering «Чат» / «Доска» over a board
          that will never draw anything is worse than no control. */}
      {!wide && !navActive && !noBoard ? (
        <div className="tabs-bar">
          <MobileTabs pane={pane} onChange={setPane} />
        </div>
      ) : null}

      {/* The navigation mode counts as an OPEN panel on wide layouts, and it
          has to: a collapsed `.app__board` is `opacity: 0; pointer-events:
          none` (base.css), so a map given the whole screen would be given an
          invisible one. It is also the layout nav wants — the chat narrows to
          `--chat-width`, which is the right rail §2.2 asks for. Below the
          breakpoint the class is never set, by dev's own rule and because
          `.app__body--panel-open .app__chat` outranks the mobile reset on
          specificity rather than on source order.

          `app__body--no-board` (`noBoard`) is the fourth state: the chat
          column is centred rather than claiming the row edge-to-edge or
          sharing it with a panel, because there is no panel to share it with
          — see `.app__body--no-board .app__chat` in `base.css`. */}
      <div
        className={cn(
          'app__body',
          wide && (panelOpen || navActive) && 'app__body--panel-open',
          noBoard && 'app__body--no-board',
        )}
      >
        <section
          className="app__chat"
          hidden={!noBoard && !wide && !navActive && pane !== 'chat'}
          aria-label="Разговор"
        >
          {!wide && navActive ? <NavSheet /> : null}
          <ErrorBoundary label="chat" recover>
            <Thread
              sendDisabled={sendDisabled}
              onQuestionnaireComplete={(resume, summary) => void submitResume(resume, summary || undefined)}
              onQuestionnaireExit={(text, answered, skipped) => void submitResume(exitedResume(text, answered, skipped), text)}
            />
          </ErrorBoundary>
          <Composer onSend={(text) => enqueueOrSend(text)} onStop={stop} sendDisabled={sendDisabled} />
        </section>

        {/* Mobile: unchanged, always mounted, toggled by `hidden` (§11.3).
            Wide: does not exist in the markup until the board's first
            non-structural element (`panelEverAppeared`) — collapsing it
            afterwards is a CSS state (`.app__body--panel-open` plus the
            `inert` DOM property set in the effect above), not an unmount, so
            the slide keeps its own DOM node to animate. `inert`, not
            `aria-hidden`: the latter only hides the subtree from assistive
            tech and leaves its buttons in the tab order.

            **`navActive` mounts it unconditionally.** In the navigation mode
            the board IS the screen, and gating the map on a panel flag the
            visitor may have set five minutes ago would leave the guide leading
            with nothing to lead on. The map card is board content, so
            `noticeContent()` flips `everAppeared` anyway — this is the belt
            that does not depend on the order those two land in.

            **`noBoard` never mounts it at all** — `peterhof_agent`'s only A2UI
            is `region:footer` chips (#127; `speaksA2ui` is false, and
            `hasBoardContent` reads main/side only) and it ships no `attachments`, so
            `panelEverAppeared` can never flip for it; excluded by name rather
            than left to that fact alone, since a future card type this
            graph's own plan does not mention yet must not silently reopen a
            board this layout has none for. */}
        {!noBoard && (!wide || panelEverAppeared || navActive) ? (
          <section
            ref={boardSection}
            className="app__board"
            hidden={!wide && !navActive && pane !== 'board'}
            aria-label={navActive ? 'Карта' : 'Доска'}
          >
            <Board />
            {navCard ? (
              <ErrorBoundary label="nav">
                <NavOverlay card={navCard} />
              </ErrorBoundary>
            ) : null}
          </section>
        ) : null}
      </div>

      {toast ? (
        <button
          type="button"
          className="toast"
          onClick={() => {
            setPane('board');
            setToast(null);
          }}
        >
          {toast} ↗
        </button>
      ) : null}
    </div>
  );
}
